// src/pages/customer/BlogDetail.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { getBlogById } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { format } from "date-fns";
import { FiArrowLeft, FiUser, FiTag, FiShare2, FiCheck } from "react-icons/fi";

export default function BlogDetail() {
  const { id } = useParams();
  const [blog,    setBlog]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(false);

  useEffect(() => {
    getBlogById(id).then((b) => { setBlog(b); setLoading(false); });
  }, [id]);

  const handleShare = async () => {
    const shareData = { title: blog?.title, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled, ignore */ }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* clipboard unavailable, ignore */ }
    }
  };

  if (loading) return <Spinner center />;

  if (!blog) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <Helmet>
        <title>Article Not Found | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>
      <p className="text-5xl mb-4">🌿</p>
      <h2 className="text-xl font-semibold text-gray-700 mb-2">Article not found</h2>
      <p className="text-gray-400 text-sm mb-6">It may have been moved or removed.</p>
      <Link to="/blog" className="btn-primary inline-flex items-center gap-2">
        <FiArrowLeft className="w-4 h-4" /> Back to Blog
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{blog.title ? `${blog.title} | Tripura Agro` : "Tripura Agro Blog"}</title>
        <meta
          name="description"
          content={
            blog.excerpt ||
            blog.content?.replace(/<[^>]+>/g, "").slice(0, 160) ||
            "Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
          }
        />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

        {/* ── Hero image ── */}
        {blog.image && (
          <div className="w-full h-48 sm:h-72 md:h-80 lg:h-96 rounded-2xl overflow-hidden mb-8 bg-primary-100">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* ── Category ── */}
        {blog.category && (
          <span className="inline-flex items-center gap-1.5 bg-leaf-50 text-leaf-700 text-xs
                           font-semibold px-3 py-1 rounded-full uppercase tracking-wide mb-4">
            <FiTag className="w-3 h-3" /> {blog.category}
          </span>
        )}

        {/* ── Title ── */}
        <h1 className="font-display text-xl sm:text-2xl md:text-[1.5rem] font-bold text-gray-900 leading-tight mb-3 line-height"   style={{
                         fontFamily: "var(--custom-body-font-family)" ,}}>
          {blog.title}
        </h1>

        {/* ── Date ── */}
        {blog.createdAt?.toDate && (
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-5">
            {format(blog.createdAt.toDate(), "MMMM d, yyyy")}
          </p>
        )}

        {/* ── Author + Share row ── */}
        <div className="flex items-center justify-between flex-wrap gap-3 pb-6 border-b border-gray-100 mb-8">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
              {blog.author?.[0]?.toUpperCase() || <FiUser className="w-3.5 h-3.5" />}
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              {blog.author || "Editorial Team"}
            </span>
          </div>

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-600
                       hover:text-primary-600 transition-colors"
          >
            {copied ? (
              <><FiCheck className="w-4 h-4" /> Copied</>
            ) : (
              <><FiShare2 className="w-4 h-4" /> Share</>
            )}
          </button>
        </div>

        {/* ── Rich-text content ── */}
        <div
          className="max-w-none text-gray-700 text-[15px] sm:text-base leading-relaxed
                     [&_h1]:font-display [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-8 [&_h1]:mb-4
                     [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-7 [&_h2]:mb-3
                     [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-3
                     [&_p]:mb-4 [&_p]:leading-relaxed
                     [&_a]:text-primary-600 [&_a]:no-underline [&_a:hover]:underline
                     [&_strong]:font-semibold [&_strong]:text-gray-900
                     [&_em]:italic
                     [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-1.5
                     [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:space-y-1.5
                     [&_li]:leading-relaxed
                     [&_blockquote]:border-l-4 [&_blockquote]:border-primary-400 [&_blockquote]:bg-primary-50
                     [&_blockquote]:py-2 [&_blockquote]:px-4 [&_blockquote]:rounded-r-lg [&_blockquote]:italic
                     [&_blockquote]:text-gray-600 [&_blockquote]:my-5
                     [&_img]:rounded-xl [&_img]:my-5 [&_img]:w-full
                     [&_hr]:my-8 [&_hr]:border-gray-200"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* ── Footer ── */}
        <div className="mt-12 pt-8 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/blog"
            className="btn-secondary inline-flex items-center gap-2 text-sm"
          >
            <FiArrowLeft className="w-4 h-4" /> More Articles
          </Link>

          <p className="text-xs text-gray-400">Found this useful? Share it with a fellow farmer.</p>
        </div>
      </div>
    </div>
  );
}